import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../lib/api';
import { Button } from '../ui/controls';
import { ConfirmDialog } from '../ui/dialog';
import { ErrorNotice, useToast } from '../ui/feedback';
import { Menu } from '../ui/menu';
import { DataTable, PageHead, Tabs } from '../ui/table';
import { DefList, Mono, Num, Tag, Time, UserStatusTag, Uuid, VerifiedTag } from '../ui/values';
import { ActivityTab, AreasTab, AuditTab, BackLink, ChestTab, PlansTab } from './common';
import { Level } from './OverviewPage';
import { paths } from './paths';
import type { AdminUserDetail } from './types';

/**
 * Spieler-Detail (design.md §6, Revision R1): Definitionsliste mit dem Minecraft-Account,
 * Reiter Kisten · Geräte · Ereignisse · Audit.
 */

type TabKey = 'chests' | 'devices' | 'activity' | 'areas' | 'plans' | 'events' | 'audit';
type Action = 'suspend' | 'unsuspend' | 'sessions' | 'grant' | 'revoke';

export function PlayerDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>('chests');
  const [action, setAction] = useState<Action | null>(null);
  const key = ['admin', 'user', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminUserDetail>(`/admin/users/${id}`) });
  const u = q.data;
  const name = u?.name ?? 'Spieler';

  const run = async (path: string, method: 'post' | 'delete', reason: string, done: string) => {
    const res = method === 'post' ? await api.post<AdminUserDetail>(path, { reason }) : await api.delete<AdminUserDetail>(path, { reason });
    if (res && 'id' in res) qc.setQueryData(key, res);
    else await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    await qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    toast(done);
  };

  if (q.isError) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!u) return <p className="muted">Lädt…</p>;

  const dialogs: Record<string, Parameters<typeof ConfirmDialog>[0] | undefined> = {
    suspend: {
      open: true,
      title: 'Spieler sperren',
      consequence: `${name} kann sich nicht mehr anmelden, die Mod funktioniert sofort nicht mehr. Kisten bleiben erhalten.`,
      confirmText: name,
      confirmLabel: 'Sperren',
      onConfirm: (r) => run(`/admin/users/${id}/suspend`, 'post', r, `${name} gesperrt.`),
      onClose: () => setAction(null),
    },
    unsuspend: {
      open: true,
      title: 'Sperre aufheben',
      consequence: `${name} kann die Mod wieder benutzen.`,
      confirmLabel: 'Entsperren',
      onConfirm: (r) => run(`/admin/users/${id}/unsuspend`, 'post', r, `${name} entsperrt.`),
      onClose: () => setAction(null),
    },
    sessions: {
      open: true,
      title: 'Alle Sessions widerrufen',
      consequence: `Meldet ${name} im Browser und auf ${u.devices.length} Gerät(en) ab. Die Mod meldet sich danach im Spiel neu an.`,
      confirmLabel: 'Widerrufen',
      onConfirm: (r) => run(`/admin/users/${id}/revoke-sessions`, 'post', r, 'Sessions widerrufen.'),
      onClose: () => setAction(null),
    },
    grant: {
      open: true,
      title: 'Admin-Recht vergeben',
      consequence: `${name} bekommt vollen Zugriff auf den Admin-Bereich.`,
      confirmText: name,
      confirmLabel: 'Admin machen',
      onConfirm: (r) => run(`/admin/users/${id}/admin`, 'post', r, `${name} ist jetzt Admin.`),
      onClose: () => setAction(null),
    },
    revoke: {
      open: true,
      title: 'Admin-Recht entziehen',
      consequence: `${name} verliert den Zugriff auf den Admin-Bereich.`,
      confirmText: name,
      confirmLabel: 'Entziehen',
      onConfirm: (r) => run(`/admin/users/${id}/admin`, 'delete', r, 'Admin-Recht entzogen.'),
      onClose: () => setAction(null),
    },
  };
  const dialog = action ? dialogs[action] : undefined;

  return (
    <div className="stack-lg">
      <PageHead
        back={<BackLink to={paths.players} label="Spieler" />}
        title={
          <>
            {name} <UserStatusTag status={u.status} /> {u.isAdmin && <Tag tone="neutral">Admin</Tag>}
          </>
        }
        actions={
          <>
            {u.status === 'suspended' ? (
              <Button onClick={() => setAction('unsuspend')}>Entsperren…</Button>
            ) : null}
            <Menu
              items={[
                ...(u.status !== 'suspended' ? [{ label: 'Sperren…', danger: true, onSelect: () => setAction('suspend') }] : []),
                { label: 'Alle Sessions widerrufen…', danger: true, onSelect: () => setAction('sessions') },
                u.isAdmin
                  ? { label: 'Admin-Recht entziehen…', danger: true, onSelect: () => setAction('revoke') }
                  : { label: 'Admin-Recht vergeben…', onSelect: () => setAction('grant') },
              ]}
            />
          </>
        }
      />

      <DefList
        items={[
          [
            'Minecraft',
            u.mcAccount ? (
              <>
                <b>{u.mcAccount.name}</b> <VerifiedTag verified={u.mcAccount.verified} />
              </>
            ) : (
              <span className="muted">–</span>
            ),
          ],
          ['MC-UUID', u.mcAccount ? <Uuid value={u.mcAccount.uuid} /> : <span className="muted">–</span>],
          [
            'Discord',
            u.discord ? (
              <>
                {u.discord.name ?? '–'} <Mono value={u.discord.id} />
              </>
            ) : (
              <span className="muted">nicht verknüpft</span>
            ),
          ],
          ['Interne ID', <Uuid value={u.id} />],
          ['Team', u.team ? <Link to={paths.team(u.team.id)}>{u.team.name}{u.team.isOwner ? ' (Besitzer)' : ''}</Link> : <span className="muted">–</span>],
          ['Kisten', <Num value={u.chestCount} />],
          ['Registriert', <Time iso={u.createdAt} absolute />],
          ['Zuletzt aktiv', u.lastSeenAt ? <Time iso={u.lastSeenAt} absolute /> : <span className="muted">–</span>],
        ]}
      />

      <div className="stack">
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'chests', label: 'Kisten', count: u.chestCount },
            { key: 'devices', label: 'Geräte', count: u.devices.length },
            { key: 'activity', label: 'Aktivität' },
            // Areas und Pläne gehören dem Team; nur Spieler ohne Team haben eigene
            ...(u.team ? [] : [{ key: 'areas' as const, label: 'Areas' }, { key: 'plans' as const, label: 'Sortierpläne' }]),
            { key: 'events', label: 'Ereignisse', count: u.recentEvents.length },
            { key: 'audit', label: 'Audit' },
          ]}
        />
        {tab === 'chests' && <ChestTab query={{ ownerId: id }} owner={false} />}
        {tab === 'activity' && <ActivityTab userId={id} />}
        {tab === 'areas' && <AreasTab query={{ ownerId: id }} />}
        {tab === 'plans' && <PlansTab query={{ ownerId: id }} />}
        {tab === 'devices' && (
          <DataTable
            label="Geräte"
            rows={u.devices}
            rowKey={(d) => d.id}
            empty="Auf keinem Gerät angemeldet."
            columns={[
              { key: 'mc', header: 'MC-Account', cell: (d) => d.mcAccount.name },
              { key: 'client', header: 'Mod', cell: (d) => (d.clientVersion ? <span className="mono">{d.clientVersion}</span> : <span className="muted">–</span>) },
              { key: 'linked', header: 'verknüpft', hide: 'sm', cell: (d) => <Time iso={d.linkedAt} /> },
              { key: 'used', header: 'zuletzt aktiv', cell: (d) => <Time iso={d.lastUsedAt} /> },
            ]}
          />
        )}
        {tab === 'events' && (
          <DataTable
            label="Ereignisse"
            rows={u.recentEvents}
            rowKey={(e) => e.id}
            empty="Keine Ereignisse."
            columns={[
              { key: 'lvl', header: 'Level', cell: (e) => <Level level={e.level} /> },
              { key: 'cat', header: 'Kategorie', cell: (e) => <span className="mono">{e.category}</span> },
              { key: 'msg', header: 'Meldung', cell: (e) => e.message },
              { key: 'at', header: 'Zeit', cell: (e) => <Time iso={e.createdAt} /> },
            ]}
          />
        )}
        {tab === 'audit' && <AuditTab targetType="user" targetId={id} />}
      </div>

      {dialog && <ConfirmDialog {...dialog} />}
    </div>
  );
}
